import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { categoryKeys, type CategoryListQuery } from '@/services/queryKeys';
import { categoryService } from '@/services/category.service';
import type { CreateCategoryInput, UpdateCategoryInput } from '@erpgold/shared';

export function useCategoryList(query: CategoryListQuery = {}) {
  return useQuery({
    queryKey: categoryKeys.list(query),
    queryFn: () => categoryService.list(query),
    placeholderData: (prev) => prev,
    retry: 0,
  });
}

export function useCategoryTree() {
  return useQuery({
    queryKey: categoryKeys.tree(),
    queryFn: () => categoryService.tree(),
    retry: 0,
  });
}

export function useCategoryDetail(id: string | undefined) {
  return useQuery({
    queryKey: categoryKeys.detail(id ?? '__none__'),
    queryFn: () => categoryService.getById(id!),
    enabled: !!id,
    retry: 0,
  });
}

export function useCreateCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateCategoryInput) => categoryService.create(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: categoryKeys.all }),
  });
}

export function useUpdateCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateCategoryInput }) =>
      categoryService.update(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: categoryKeys.all }),
  });
}

export function useDeleteCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => categoryService.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: categoryKeys.all }),
  });
}

export function useReorderCategories() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (orders: { id: string; ordering: number }[]) =>
      categoryService.reorder(orders),
    onSuccess: () => qc.invalidateQueries({ queryKey: categoryKeys.all }),
  });
}
